import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders } from '../../utils/authUtils';
import { createApiUrl } from '../../utils/apiConfig';
import CustomDatePicker from '../CustomDatePicker';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const QueryBuilderStatistics = forwardRef(({ currentUser }, ref) => {
  const { t } = useTranslation();

  // State
  const [chartType, setChartType] = useState('bar');
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [yAxisType, setYAxisType] = useState('count'); // 'count' or 'size'
  const [timeInterval, setTimeInterval] = useState('month'); // 'date', 'week', 'month', 'quarter', 'year'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [chartData, setChartData] = useState([]);
  const [total, setTotal] = useState(0);
  const [allModalities, setAllModalities] = useState([]);
  const [responseYAxisType, setResponseYAxisType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [columnsLoading, setColumnsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('pdf'); // 'pdf' or 'csv'
  const [queryName, setQueryName] = useState('');
  
  // Selection and hover state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  
  // Dynamic filters state
  const [dynamicFilters, setDynamicFilters] = useState([
    { id: Date.now(), column: '', operator: 'include', value: '' }
  ]);

  // Load available columns on mount
  useEffect(() => {
    loadAvailableColumns();
  }, []);

  // Reset selections when Y-axis type changes
  useEffect(() => {
    setSelectedCategories([]);
    setHoveredCategory(null);
  }, [yAxisType]);

  const loadAvailableColumns = async () => {
    try {
      setColumnsLoading(true);
      const response = await fetch(createApiUrl('/statistics/query-builder/columns'), {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to load columns');
      }

      const data = await response.json();
      setAvailableColumns(data.columns || []);

      // Set default column
      if (data.columns && data.columns.length > 0) {
        setSelectedColumn(data.columns[0].value);
      }
    } catch (err) {
      console.error('Error loading columns:', err);
      setError(t('statistics.queryBuilder.errorLoadingColumns'));
    } finally {
      setColumnsLoading(false);
    }
  };

  const executeQuery = async () => {
    // Validation
    if (yAxisType === 'count' && !selectedColumn) {
      setError(t('statistics.queryBuilder.selectColumn'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        chart_type: chartType,
        y_axis_type: yAxisType
      });

      // For size-based queries, always use time grouping
      if (yAxisType === 'size') {
        params.append('group_by_column', 'studyDate');
        params.append('time_interval', timeInterval);
      } else {
        params.append('group_by_column', selectedColumn);
      }

      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }

      // Add dynamic filters (only those with all fields filled)
      const validFilters = dynamicFilters.filter(f => f.column && f.value);
      if (validFilters.length > 0) {
        params.append('filters', JSON.stringify(validFilters));
      }

      const response = await fetch(
        createApiUrl(`/statistics/query-builder/query?${params.toString()}`),
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        throw new Error('Query execution failed');
      }

      const result = await response.json();
      setChartData(result.data || []);
      setTotal(result.total || 0);
      setAllModalities(result.all_modalities || []);
      setResponseYAxisType(result.y_axis_type || null);
    } catch (err) {
      console.error('Error executing query:', err);
      setError(t('statistics.queryBuilder.errorExecutingQuery'));
    } finally {
      setLoading(false);
    }
  };

  // Dynamic filter management
  const addFilter = () => {
    setDynamicFilters([...dynamicFilters, { 
      id: Date.now(), 
      column: '', 
      operator: 'include', 
      value: '' 
    }]);
  };

  const removeFilter = (id) => {
    if (dynamicFilters.length > 1) {
      setDynamicFilters(dynamicFilters.filter(f => f.id !== id));
    }
  };

  const updateFilter = (id, field, value) => {
    setDynamicFilters(dynamicFilters.map(f => 
      f.id === id ? { ...f, [field]: value } : f
    ));
  };

  // Open export dialog
  const handlePdfDownload = () => {
    setExportType('pdf');
    setQueryName('');
    setExportDialogOpen(true);
  };

  const handleCsvDownload = () => {
    setExportType('csv');
    setQueryName('');
    setExportDialogOpen(true);
  };

  // Actual export functions (called after dialog confirmation)
  const performPdfExport = (name) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    const chartTypeLabel = chartType === 'bar' ? t('statistics.queryBuilder.chartTypeBar') : chartType === 'line' ? t('statistics.queryBuilder.chartTypeLine') : t('statistics.queryBuilder.chartTypeDonut');
    const timeIntervalLabels = {
      date: t('statistics.queryBuilder.timeDay'),
      week: t('statistics.queryBuilder.timeWeek'),
      month: t('statistics.queryBuilder.timeMonth'),
      quarter: t('statistics.queryBuilder.timeQuarter'),
      year: t('statistics.queryBuilder.timeYear')
    };
    const allDates = t('statistics.queryBuilder.pdf.allDates');
    
    // Title - Use custom name
    doc.setFontSize(16);
    doc.text(name || t('statistics.queryBuilder.pdfTitle'), 14, 20);
    
    // Parameters
    doc.setFontSize(10);
    doc.text(`${t('statistics.queryBuilder.pdf.chartTypeLabel')} ${chartTypeLabel}`, 14, 30);
    
    if (responseYAxisType === 'size') {
      doc.text(`${t('statistics.queryBuilder.pdf.yAxisLabel')} ${t('statistics.queryBuilder.yAxisFileSize')}`, 14, 36);
      doc.text(`${t('statistics.queryBuilder.pdf.timeIntervalLabel')} ${timeIntervalLabels[timeInterval] || timeInterval}`, 14, 42);
      doc.text(`${t('statistics.queryBuilder.pdf.periodLabel')} ${startDate || allDates} - ${endDate || allDates}`, 14, 48);
      doc.text(`${t('statistics.queryBuilder.totalFileSize')} ${formatBytes(total)}`, 14, 54);
    } else {
      doc.text(`${t('statistics.queryBuilder.pdf.groupByLabel')} ${availableColumns.find(c => c.value === selectedColumn)?.label || selectedColumn}`, 14, 36);
      doc.text(`${t('statistics.queryBuilder.pdf.periodLabel')} ${startDate || allDates} - ${endDate || allDates}`, 14, 42);
      doc.text(`${t('statistics.queryBuilder.totalCount')} ${total.toLocaleString()}`, 14, 48);
    }
    
    // Table
    let tableData, tableHeaders;
    
    if (responseYAxisType === 'size') {
      // For size-based queries: show periods with total size
      tableData = chartData.map((item, index) => [
        index + 1,
        item.category,
        item.formatted || formatBytes(item.count),
        ((item.count / total) * 100).toFixed(1) + '%'
      ]);
      tableHeaders = [['#', t('statistics.queryBuilder.pdf.timePeriod'), t('statistics.queryBuilder.pdf.fileSize'), t('statistics.queryBuilder.pdf.share')]];
    } else {
      // For count-based queries
      tableData = chartData.map((item, index) => [
        index + 1,
        item.category,
        item.count.toLocaleString(),
        ((item.count / total) * 100).toFixed(1) + '%'
      ]);
      tableHeaders = [['#', t('statistics.queryBuilder.pdf.category'), t('statistics.queryBuilder.pdf.count'), t('statistics.queryBuilder.pdf.share')]];
    }
    
    doc.autoTable({
      startY: responseYAxisType === 'size' ? 61 : 55,
      head: tableHeaders,
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [66, 139, 202] }
    });
    
    // Footer
    const timestamp = new Date().toLocaleString();
    const footerY = doc.internal.pageSize.height - 10;
    doc.setFontSize(8);
    doc.text(`${t('statistics.queryBuilder.pdf.createdOn')} ${timestamp}`, 14, footerY);
    
    // Use custom name for filename
    const defaultFilename = t('statistics.queryBuilder.csvFilename');
    const filename = name ? `${name.replace(/[^a-z0-9_-]/gi, '_').toLowerCase()}.pdf` : `${defaultFilename}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  };

  const performCsvExport = (name) => {
    const columnLabel = availableColumns.find(c => c.value === selectedColumn)?.label || selectedColumn;
    
    // Use custom name in first line
    let csvContent;
    
    if (responseYAxisType === 'size') {
      // For size-based queries
      csvContent = [
        [name || t('statistics.queryBuilder.pdfTitle')].join(';'),
        [t('statistics.queryBuilder.pdf.timePeriod'), t('statistics.queryBuilder.pdf.fileSizeBytes'), t('statistics.queryBuilder.pdf.fileSizeFormatted'), t('statistics.queryBuilder.pdf.sharePct')].join(';'),
        ...chartData.map(item => [
          item.category,
          item.count,
          item.formatted || formatBytes(item.count),
          ((item.count / total) * 100).toFixed(1)
        ].join(';'))
      ].join('\n');
    } else {
      // For count-based queries
      csvContent = [
        [name || t('statistics.queryBuilder.pdfTitle')].join(';'),
        [t('statistics.queryBuilder.pdf.category'), t('statistics.queryBuilder.pdf.count'), t('statistics.queryBuilder.pdf.sharePct')].join(';'),
        ...chartData.map(item => [
          item.category,
          item.count,
          ((item.count / total) * 100).toFixed(1)
        ].join(';'))
      ].join('\n');
    }
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    
    // Use custom name for filename
    const defaultFilename = t('statistics.queryBuilder.csvFilename');
    const filename = name ? `${name.replace(/[^a-z0-9_-]/gi, '_').toLowerCase()}.csv` : `${defaultFilename}-${new Date().toISOString().split('T')[0]}.csv`;
    link.download = filename;
    link.click();
  };

  // Handle export confirmation
  const handleExportConfirm = () => {
    if (!queryName.trim()) {
      return;
    }

    if (exportType === 'pdf') {
      performPdfExport(queryName);
    } else {
      performCsvExport(queryName);
    }

    setExportDialogOpen(false);
    setQueryName('');
  };

  // Helper function to format bytes into human-readable size
  const formatBytes = (bytes) => {
    if (bytes >= 1_000_000_000_000) {  // TB
      return `${(bytes / 1_000_000_000_000).toFixed(2)} TB`;
    } else if (bytes >= 1_000_000_000) {  // GB
      return `${(bytes / 1_000_000_000).toFixed(2)} GB`;
    } else if (bytes >= 1_000_000) {  // MB
      return `${(bytes / 1_000_000).toFixed(2)} MB`;
    } else if (bytes >= 1_000) {  // KB
      return `${(bytes / 1_000).toFixed(2)} KB`;
    } else {
      return `${bytes} Bytes`;
    }
  };

  // Calculate total based on selection
  const calculateTotal = () => {
    if (!chartData || chartData.length === 0) return 0;
    
    let sum;
    
    // Special handling for size-based queries
    if (responseYAxisType === 'size') {
      if (selectedCategories.length === 0) {
        // No selection: return grand total across all periods and modalities
        sum = chartData.reduce((total, periodData) => {
          return total + (periodData.count || 0);
        }, 0);
      } else {
        // Sum selected modalities across all periods
        sum = chartData.reduce((total, periodData) => {
          const periodSum = selectedCategories.reduce((mSum, modality) => {
            return mSum + (periodData.modalities[modality]?.bytes || 0);
          }, 0);
          return total + periodSum;
        }, 0);
      }
      return formatBytes(sum);
    }
    
    // Standard count-based calculation
    if (selectedCategories.length === 0) {
      // No selection: return grand total
      sum = chartData.reduce((total, item) => total + item.count, 0);
    } else {
      // Sum selected categories
      sum = selectedCategories.reduce((total, category) => {
        const item = chartData.find(d => d.category === category);
        return total + (item ? item.count : 0);
      }, 0);
    }
    
    // If this is size_by_modality, format as bytes
    if (selectedColumn === 'size_by_modality') {
      return formatBytes(sum);
    }
    
    return sum.toLocaleString();
  };

  // Handle bar click for selection
  const handleBarClick = (event, params) => {
    if (!params || params.dataIndex === undefined) return;
    
    const category = chartData[params.dataIndex].category;
    
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  // Helper: Make color lighter/greyed out
  const makeColorFaded = (color) => {
    // Handle RGB format
    if (color.startsWith('rgb(')) {
      return color.replace(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/, (match, r, g, b) => {
        const avg = Math.floor((parseInt(r) + parseInt(g) + parseInt(b)) / 3);
        const lighter = Math.floor(avg + (255 - avg) * 0.7);
        return `rgb(${lighter}, ${lighter}, ${lighter})`;
      });
    }
    
    // Handle HSL format
    if (color.startsWith('hsl(')) {
      return color.replace(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/, (match, h, s, l) => {
        return `hsl(0, 0%, 80%)`;
      });
    }
    
    // Handle HEX format
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      const avg = Math.floor((r + g + b) / 3);
      const lighter = Math.floor(avg + (255 - avg) * 0.7);
      const hex = lighter.toString(16).padStart(2, '0');
      return `#${hex}${hex}${hex}`;
    }
    
    return color;
  };

  // Expose download functions to parent
  useImperativeHandle(ref, () => ({
    handlePdfDownload,
    handleCsvDownload
  }));

  // Get chart colors from CSS variables and generate additional colors if needed
  // Use a stable seed for consistent colors across re-renders
  const getChartColors = (count) => {
    const colors = [];
    
    // Get first 10 colors from CSS variables
    for (let i = 1; i <= Math.min(10, count); i++) {
      const color = getComputedStyle(document.documentElement)
        .getPropertyValue(`--rd-chart-${i}`)
        .trim();
      if (color) {
        colors.push(color);
      }
    }
    
    // Generate additional colors using Golden Ratio if needed
    if (count > 10) {
      const goldenRatio = 0.618033988749895;
      let hue = 0.5; // Fixed starting hue instead of Math.random()
      
      for (let i = 10; i < count; i++) {
        hue += goldenRatio;
        hue %= 1;
        const color = `hsl(${Math.floor(hue * 360)}, 70%, 60%)`;
        colors.push(color);
      }
    }
    
    return colors;
  };

  // Helper function to format display value (uses formatted if available, otherwise count)
  const formatDisplayValue = (item) => {
    return item.formatted || item.count;
  };

  // Render chart based on type
  const renderChart = () => {
    if (chartData.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary">
            {t('statistics.queryBuilder.noData')}
          </Typography>
        </Box>
      );
    }

    // Special rendering for size-based queries (stacked by modality)
    if (responseYAxisType === 'size') {
      const periods = chartData.map(item => item.category);
      const modalityColors = getChartColors(allModalities.length);
      
      // Create one series per modality
      const series = allModalities.map((modality, modalityIndex) => {
        const data = chartData.map(periodData => {
          return periodData.modalities[modality]?.bytes || 0;
        });
        
        const isSelected = selectedCategories.includes(modality);
        const hasSelection = selectedCategories.length > 0;
        const isHovered = hoveredCategory === modality;
        const hasHover = hoveredCategory !== null;
        
        let color = modalityColors[modalityIndex];
        if (hasHover && !isHovered) {
          color = makeColorFaded(modalityColors[modalityIndex]);
        } else if (hasSelection && !isSelected && !hasHover) {
          color = makeColorFaded(modalityColors[modalityIndex]);
        }
        
        return {
          data: data,
          label: modality,
          stack: chartType === 'bar' ? 'total' : undefined, // Only stack for bar chart
          color: color,
          curve: chartType === 'line' ? 'linear' : undefined
        };
      });
      
      // Render based on chart type
      let chartComponent;
      
      if (chartType === 'bar') {
        chartComponent = (
          <BarChart
            xAxis={[{
              scaleType: 'band',
              data: periods,
              tickLabelStyle: {
                angle: -45,
                textAnchor: 'end',
                fontSize: 10
              }
            }]}
            yAxis={[{
              label: t('statistics.queryBuilder.axisFileSize')
            }]}
            series={series}
            height={500}
            margin={{ top: 20, right: 30, bottom: 100, left: 80 }}
            slotProps={{ 
              legend: { hidden: true },
              tooltip: { trigger: 'none' }
            }}
            sx={{ 
              '& .MuiChartsLegend-root': { display: 'none !important' },
              '& .MuiChartsTooltip-root': { display: 'none !important' }
            }}
            disableAxisListener={true}
          />
        );
      } else if (chartType === 'line') {
        chartComponent = (
          <LineChart
            xAxis={[{
              scaleType: 'band',
              data: periods,
              tickLabelStyle: {
                angle: -45,
                textAnchor: 'end',
                fontSize: 10
              }
            }]}
            yAxis={[{
              label: t('statistics.queryBuilder.axisFileSize')
            }]}
            series={series}
            height={500}
            margin={{ top: 20, right: 30, bottom: 100, left: 80 }}
            slotProps={{ 
              legend: { hidden: true },
              tooltip: { trigger: 'none' }
            }}
            sx={{ 
              '& .MuiChartsLegend-root': { display: 'none !important' },
              '& .MuiChartsTooltip-root': { display: 'none !important' }
            }}
            disableAxisListener={true}
          />
        );
      } else if (chartType === 'donut') {
        // For donut: show total bytes per modality across all periods
        const donutData = allModalities.map((modality, modalityIndex) => {
          const totalBytes = chartData.reduce((sum, periodData) => {
            return sum + (periodData.modalities[modality]?.bytes || 0);
          }, 0);
          
          const isSelected = selectedCategories.includes(modality);
          const hasSelection = selectedCategories.length > 0;
          const isHovered = hoveredCategory === modality;
          const hasHover = hoveredCategory !== null;
          
          let color = modalityColors[modalityIndex];
          if (hasHover && !isHovered) {
            color = makeColorFaded(modalityColors[modalityIndex]);
          } else if (hasSelection && !isSelected && !hasHover) {
            color = makeColorFaded(modalityColors[modalityIndex]);
          }
          
          return {
            id: modalityIndex,
            value: totalBytes,
            label: modality,
            color: color
          };
        });
        
        chartComponent = (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <PieChart
              series={[{
                data: donutData,
                innerRadius: 80,
                outerRadius: 120,
                paddingAngle: 2,
                cornerRadius: 5,
                highlightScope: { faded: 'global', highlighted: 'item' }
              }]}
              width={800}
              height={500}
              margin={{ top: 20, right: 200, bottom: 20, left: 20 }}
              slotProps={{ 
                legend: { hidden: true },
                tooltip: { trigger: 'none' }
              }}
              sx={{ 
                '& .MuiChartsLegend-root': { display: 'none !important' },
                '& .MuiChartsTooltip-root': { display: 'none !important' }
              }}
              onItemClick={(event, params) => {
                if (params && params.dataIndex !== undefined) {
                  const modality = allModalities[params.dataIndex];
                  setSelectedCategories(prev => {
                    if (prev.includes(modality)) {
                      return prev.filter(c => c !== modality);
                    } else {
                      return [...prev, modality];
                    }
                  });
                }
              }}
            />
          </Box>
        );
      }
      
      return (
        <Box>
          {chartComponent}
          
          {/* Custom Legend for Modalities */}
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {allModalities.map((modality, index) => {
              const isSelected = selectedCategories.includes(modality);
              const isHovered = hoveredCategory === modality;
              
              // Calculate total bytes for this modality across all periods
              const modalityTotal = chartData.reduce((sum, periodData) => {
                return sum + (periodData.modalities[modality]?.bytes || 0);
              }, 0);
              
              return (
                <Box
                  key={modality}
                  onClick={() => {
                    setSelectedCategories(prev => {
                      if (prev.includes(modality)) {
                        return prev.filter(c => c !== modality);
                      } else {
                        return [...prev, modality];
                      }
                    });
                  }}
                  onMouseEnter={() => setHoveredCategory(modality)}
                  onMouseLeave={() => setHoveredCategory(null)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '4px 8px',
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor: isHovered ? 'action.hover' : 'transparent',
                    border: isSelected ? '2px solid' : '1px solid transparent',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: getChartColors(allModalities.length)[index], // Always use original color
                      borderRadius: 0.5
                    }}
                  />
                  <Typography variant="body2">
                    {modality}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({formatBytes(modalityTotal)})
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    }

    // Standard count-based rendering
    const categories = chartData.map(item => item.category);
    const values = chartData.map(item => item.count);
    const baseColors = getChartColors(categories.length);

    if (chartType === 'bar') {
      // Create one series per category for individual coloring with hover/selection logic
      const series = categories.map((category, index) => {
        const isSelected = selectedCategories.includes(category);
        const hasSelection = selectedCategories.length > 0;
        const isHovered = hoveredCategory === category;
        const hasHover = hoveredCategory !== null;
        
        // Determine color based on hover and selection
        let color = baseColors[index];
        if (hasHover && !isHovered) {
          // When hovering, fade everything except the hovered item
          color = makeColorFaded(baseColors[index]);
        } else if (hasSelection && !isSelected && !hasHover) {
          // When selected (and not hovering), fade non-selected
          color = makeColorFaded(baseColors[index]);
        }
        
        return {
          data: values.map((v, i) => i === index ? v : null),
          label: category,
          color: color,
          stack: 'total'
        };
      });

      return (
        <Box>
          <BarChart
            xAxis={[{
              scaleType: 'band',
              data: categories,
              tickLabelStyle: {
                angle: -45,
                textAnchor: 'end',
                fontSize: 10
              }
            }]}
            yAxis={[{
              label: t('statistics.queryBuilder.axisCount')
            }]}
            series={series}
            height={500}
            margin={{ top: 20, right: 30, bottom: 100, left: 60 }}
            slotProps={{ 
              legend: { hidden: true },
              tooltip: { trigger: 'none' }
            }}
            sx={{ 
              '& .MuiChartsLegend-root': { display: 'none !important' },
              '& .MuiChartsTooltip-root': { display: 'none !important' }
            }}
            onAxisClick={handleBarClick}
            disableAxisListener={true}
          />
          
          {/* Custom Legend */}
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {chartData.map((item, index) => {
              const isSelected = selectedCategories.includes(item.category);
              const isHovered = hoveredCategory === item.category;
              
              return (
                <Box
                  key={item.category}
                  onClick={() => handleBarClick(null, { dataIndex: index })}
                  onMouseEnter={() => setHoveredCategory(item.category)}
                  onMouseLeave={() => setHoveredCategory(null)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '4px 8px',
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor: isHovered ? 'action.hover' : 'transparent',
                    border: isSelected ? '2px solid' : '1px solid transparent',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: baseColors[index],
                      borderRadius: 0.5
                    }}
                  />
                  <Typography variant="body2">
                    {item.category}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({formatDisplayValue(item)})
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    } else if (chartType === 'line') {
      return (
        <LineChart
          xAxis={[{
            scaleType: 'band',
            data: categories,
            tickLabelStyle: {
              angle: -45,
              textAnchor: 'end',
              fontSize: 10
            }
          }]}
          yAxis={[{
            label: t('statistics.queryBuilder.axisCount')
          }]}
          series={[{
            data: values,
            color: baseColors[0],
            curve: 'linear'
          }]}
          height={500}
          margin={{ top: 20, right: 30, bottom: 100, left: 60 }}
          slotProps={{ 
            legend: { hidden: true },
            tooltip: { trigger: 'none' }
          }}
          sx={{ 
            '& .MuiChartsLegend-root': { display: 'none !important' },
            '& .MuiChartsTooltip-root': { display: 'none !important' }
          }}
          disableAxisListener={true}
        />
      );
    } else if (chartType === 'donut') {
      // Apply fading logic to donut colors
      const donutData = values.map((value, index) => {
        const category = categories[index];
        const isSelected = selectedCategories.includes(category);
        const hasSelection = selectedCategories.length > 0;
        const isHovered = hoveredCategory === category;
        const hasHover = hoveredCategory !== null;
        
        let color = baseColors[index];
        if (hasHover && !isHovered) {
          color = makeColorFaded(baseColors[index]);
        } else if (hasSelection && !isSelected && !hasHover) {
          color = makeColorFaded(baseColors[index]);
        }
        
        return {
          id: index,
          value: value,
          label: category,
          color: color
        };
      });
      
      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <PieChart
              series={[{
                data: donutData,
                innerRadius: 80,
                outerRadius: 120,
                paddingAngle: 2,
                cornerRadius: 5,
                highlightScope: { faded: 'global', highlighted: 'item' }
              }]}
              width={800}
              height={500}
              margin={{ top: 20, right: 200, bottom: 20, left: 20 }}
              slotProps={{ 
                legend: { hidden: true },
                tooltip: { trigger: 'none' }
              }}
              sx={{ 
                '& .MuiChartsLegend-root': { display: 'none !important' },
                '& .MuiChartsTooltip-root': { display: 'none !important' }
              }}
              onItemClick={(event, params) => {
                if (params && params.dataIndex !== undefined) {
                  handleBarClick(null, { dataIndex: params.dataIndex });
                }
              }}
            />
          </Box>
          
          {/* Custom Legend */}
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {chartData.map((item, index) => {
              const isSelected = selectedCategories.includes(item.category);
              const isHovered = hoveredCategory === item.category;
              
              return (
                <Box
                  key={item.category}
                  onClick={() => handleBarClick(null, { dataIndex: index })}
                  onMouseEnter={() => setHoveredCategory(item.category)}
                  onMouseLeave={() => setHoveredCategory(null)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '4px 8px',
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor: isHovered ? 'action.hover' : 'transparent',
                    border: isSelected ? '2px solid' : '1px solid transparent',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: baseColors[index],
                      borderRadius: 0.5
                    }}
                  />
                  <Typography variant="body2">
                    {item.category}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({formatDisplayValue(item)})
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    }
  };

  if (columnsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('statistics.queryBuilder.title', 'Query-Builder')}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('statistics.queryBuilder.configureQuery')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
          {/* Chart Type Selection */}
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>{t('statistics.queryBuilder.chartType')}</InputLabel>
            <Select
              value={chartType}
              label={t('statistics.queryBuilder.chartType')}
              onChange={(e) => setChartType(e.target.value)}
            >
              <MenuItem value="bar">{t('statistics.queryBuilder.chartTypeBar')}</MenuItem>
              <MenuItem value="line">{t('statistics.queryBuilder.chartTypeLine')}</MenuItem>
              <MenuItem value="donut">{t('statistics.queryBuilder.chartTypeDonut')}</MenuItem>
            </Select>
          </FormControl>

          {/* Y-Axis Type Selection */}
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>{t('statistics.queryBuilder.yAxis')}</InputLabel>
            <Select
              value={yAxisType}
              label={t('statistics.queryBuilder.yAxis')}
              onChange={(e) => setYAxisType(e.target.value)}
            >
              <MenuItem value="count">{t('statistics.queryBuilder.yAxisCount')}</MenuItem>
              <MenuItem value="size">{t('statistics.queryBuilder.yAxisFileSize')}</MenuItem>
            </Select>
          </FormControl>

          {/* Column Selection (only for count-based queries) */}
          {yAxisType === 'count' && (
            <FormControl sx={{ minWidth: 250 }} size="small">
              <InputLabel>{t('statistics.queryBuilder.groupBy')}</InputLabel>
              <Select
                value={selectedColumn}
                label={t('statistics.queryBuilder.groupBy')}
                onChange={(e) => setSelectedColumn(e.target.value)}
              >
                {availableColumns
                  .filter(col => col.value !== 'bytes' && col.value !== 'size_by_modality')
                  .map((column) => (
                    <MenuItem key={column.value} value={column.value}>
                      {column.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {/* Time Interval Selection (only for size-based queries) */}
          {yAxisType === 'size' && (
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>{t('statistics.queryBuilder.timeInterval')}</InputLabel>
              <Select
                value={timeInterval}
                label={t('statistics.queryBuilder.timeInterval')}
                onChange={(e) => setTimeInterval(e.target.value)}
              >
                <MenuItem value="date">{t('statistics.queryBuilder.timeDay')}</MenuItem>
                <MenuItem value="week">{t('statistics.queryBuilder.timeWeek')}</MenuItem>
                <MenuItem value="month">{t('statistics.queryBuilder.timeMonth')}</MenuItem>
                <MenuItem value="quarter">{t('statistics.queryBuilder.timeQuarter')}</MenuItem>
                <MenuItem value="year">{t('statistics.queryBuilder.timeYear')}</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Date Range */}
          <CustomDatePicker
            label={t('statistics.queryBuilder.startDate')}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            sx={{ minWidth: 180 }}
          />

          <CustomDatePicker
            label={t('statistics.queryBuilder.endDate')}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            sx={{ minWidth: 180 }}
          />

        </Box>

        {/* Dynamic Filters Section */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('statistics.queryBuilder.additionalFilters')}
            </Typography>
            <IconButton 
              size="small" 
              onClick={addFilter}
              sx={{ color: 'primary.main' }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>

          {dynamicFilters.map((filter, index) => (
            <Box 
              key={filter.id} 
              sx={{ 
                display: 'flex', 
                gap: 1, 
                mb: 1, 
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
              {/* Column Selection */}
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel size="small">{t('statistics.queryBuilder.filterField')}</InputLabel>
                <Select
                  size="small"
                  value={filter.column}
                  label={t('statistics.queryBuilder.filterField')}
                  onChange={(e) => updateFilter(filter.id, 'column', e.target.value)}
                >
                  {availableColumns.map((column) => (
                    <MenuItem key={column.value} value={column.value}>
                      {column.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Operator Selection */}
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel size="small">{t('statistics.queryBuilder.filterAction')}</InputLabel>
                <Select
                  size="small"
                  value={filter.operator}
                  label={t('statistics.queryBuilder.filterAction')}
                  onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                >
                  <MenuItem value="include">{t('statistics.queryBuilder.filterInclude')}</MenuItem>
                  <MenuItem value="exclude">{t('statistics.queryBuilder.filterExclude')}</MenuItem>
                </Select>
              </FormControl>

              {/* Value Input */}
              <TextField
                size="small"
                label={t('statistics.queryBuilder.filterValue')}
                value={filter.value}
                onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                sx={{ minWidth: 200 }}
                placeholder={t('statistics.queryBuilder.filterPlaceholder')}
              />

              {/* Remove Button */}
              <IconButton 
                size="small"
                onClick={() => removeFilter(filter.id)}
                disabled={dynamicFilters.length === 1}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Execute Button and Grand Total */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            className="button"
            onClick={executeQuery}
            disabled={loading || !selectedColumn}
          >
            {loading ? <CircularProgress size={24} /> : t('statistics.queryBuilder.executeQuery')}
          </Button>

          {/* Grand Total Display */}
          {chartData.length > 0 && (
            <Box sx={{ ml: 'auto', textAlign: 'right', minWidth: '150px' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {selectedCategories.length === 0 
                  ? t('statistics.queryBuilder.grandTotal') 
                  : t('statistics.queryBuilder.selection')}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {calculateTotal()}
              </Typography>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {chartData.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>
                {responseYAxisType === 'size' ? t('statistics.queryBuilder.totalFileSize') : t('statistics.queryBuilder.totalCount')}
              </strong>{' '}
              {responseYAxisType === 'size' ? formatBytes(total) : `${total.toLocaleString()} ${t('statistics.queryBuilder.studies')}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>
                {responseYAxisType === 'size' ? t('statistics.queryBuilder.timePeriods') : `${t('statistics.queryBuilder.categories')}:`}
              </strong>{' '}
              {chartData.length}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Chart */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('statistics.queryBuilder.result')}
        </Typography>
        {renderChart()}
      </Paper>

      {/* Export Name Dialog */}
      <Dialog 
        open={exportDialogOpen} 
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('statistics.queryBuilder.exportDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('statistics.queryBuilder.exportDialog.description')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={t('statistics.queryBuilder.exportDialog.nameLabel')}
            value={queryName}
            onChange={(e) => setQueryName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && queryName.trim()) {
                handleExportConfirm();
              }
            }}
            placeholder={t('statistics.queryBuilder.exportDialog.namePlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setExportDialogOpen(false)}
            className="button"
          >
            {t('statistics.queryBuilder.exportDialog.cancel')}
          </Button>
          <Button 
            onClick={handleExportConfirm}
            className="button"
            disabled={!queryName.trim()}
          >
            {exportType === 'pdf' ? t('statistics.queryBuilder.exportDialog.createPdf') : t('statistics.queryBuilder.exportDialog.createCsv')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

QueryBuilderStatistics.displayName = 'QueryBuilderStatistics';

export default QueryBuilderStatistics;
